import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import Promise from 'bluebird';
import { NodeProps, NodeUpdatedHandler, NodeOperations, TreeView } from '@bingads-webui-react/tree-view';
import { DefaultOnScrollOptions } from './constants';

export class CustomerHierarchyTreeView extends React.Component {
  constructor(props) {
    super(props);
    if (this.props.enableOnScrollLoad) {
      this.debounceLoadMoreRows = _.debounce(
        this.loadMoreRows,
        this.props.onScrollOptions.debounceOnScrollInterval
      );
    }
  }

  componentDidMount = () => {
    if ((this.props.nodes || []).length === 0) {
      this.props.onMountCallback();
      this.fetchRootLevelHierarchy();
    }
  }

  // Fetch the hierarchy tree under the node
  onNodeExpanded = (nodes, node) => {
    const childrenCount = (node.children || []).length;
    if (childrenCount === 0 || childrenCount < node.totalChildCount) {
      this.asyncLoadHierarchy({ customerId: node.id, startIndex: childrenCount });
    }
    return NodeUpdatedHandler(nodes, node);
  };

  fetchRootLevelHierarchy = () =>
    this.asyncLoadHierarchy({ customerId: this.props.rootCustomerId });

  asyncLoadHierarchy = ({ customerId, startIndex = null }) => {
    this.props.fetchCustomerHierarchy({
      cid: customerId,
      start: startIndex,
    });
  }

  hierarchyNodeOperations = () => ({
    [NodeOperations.EXPANDED]: this.onNodeExpanded,
  });

  /**
  * callback when the onscroll event is triggered
  * We then select the expanded nodes with child count less than max limit.
  * select the deepest node among them to fetch more children under the node
  * @param {object} parameters passed to the callback when onscroll event is triggered
  * @param {array} nodes - array of flattened tree nodes
  * @returns {Promise} promise object to fetch hierarchy response
  */
  loadMoreRows = ({ nodes }) => {
    const filteredNodes = _.filter(nodes, node => this.isRowPartiallyLoaded({ node }));
    if (filteredNodes === null || filteredNodes.length === 0) {
      return Promise.resolve();
    }
    const deepestNode = _.max(filteredNodes, node => node.deepness);

    return this.asyncLoadHierarchy({
      customerId: deepestNode.id,
      startIndex: (deepestNode.children || []).length,
    });
  }

  // return true if the node is in expanded state and not all children have been loaded yet
  isRowPartiallyLoaded = ({ node }) => {
    const childCount = (node.children || []).length;
    return (node.state || {}).expanded &&
      (childCount === 0 || childCount < node.totalChildCount);
  }

  render() {
    return (
      <TreeView
        nodes={this.props.nodes}
        filterNode={this.props.filterNode}
        onChange={this.props.onChange}
        nodeRenderer={this.props.nodeRenderer}
        treeViewClassNames={this.props.hierarchyClassNames}
        updateTypeHandlers={this.hierarchyNodeOperations()}
        autoHeight={this.props.autoHeight}
        iconsClassNameMap={this.props.iconsClassNameMap}
        scrollToId={this.props.scrollToId}
        enableOnScrollLoad={this.props.enableOnScrollLoad}
        isRowLoaded={() => false}
        loadMoreRows={this.debounceLoadMoreRows}
        onScrollOptions={_.extend({}, DefaultOnScrollOptions, this.props.onScrollOptions)}
        cellHeight={this.props.cellHeight}
        getClassNames={this.props.getClassNames}
        optimizeRendering
      />
    );
  }
}

CustomerHierarchyTreeView.propTypes = {
  /**
   * Array of nodes to be displayed in the tree view component
   */
  nodes: PropTypes.arrayOf(NodeProps).isRequired,
  /**
  * function to render nodes
   */
  nodeRenderer: PropTypes.func.isRequired,
  /**
   * function to filter the list of nodes to be displayed
   */
  filterNode: PropTypes.func.isRequired,
  /**
   * callback to update nodes when any change in node state is done
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Promise to Fetch Customer Hierarchy Entities
  */
  fetchCustomerHierarchy: PropTypes.func.isRequired,
  /**
   * Function to perform extra operation after the tree view is loaded
  */
  onMountCallback: PropTypes.func,
  /**
   * root customer id or the login user's parent customer id
  */
  rootCustomerId: PropTypes.number.isRequired,
  /**
   * entity id to scroll on mounting
  */
  scrollToId: PropTypes.number,
  /**
   * boolean to indicate to set height based on number of nodes
  */
  autoHeight: PropTypes.bool,
  /**
   * each node height, will be used to calculate the height when autoHeight is enabled.
   */
  cellHeight: PropTypes.number,
  /**
   * bool to indicate if Loading Rows on Scroll is enabled
  */
  enableOnScrollLoad: PropTypes.bool,
  /**
   * On Scroll callback options
  */
  onScrollOptions: PropTypes.shape({
    threshold: PropTypes.number,
    batchSize: PropTypes.number,
    debounceOnScrollInterval: PropTypes.number,
  }),
  /**
   * i18n localization object
  */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * Class Names for the Tree view
  */
  hierarchyClassNames: PropTypes.string,
  /**
   * Class Names mapping for the Expand/ Collapse Icons
  */
  iconsClassNameMap: PropTypes.shape({
    expanded: PropTypes.string,
    collapsed: PropTypes.string,
    lastChild: PropTypes.string,
  }),
  /**
   * return node specific array of class names
  */
  getClassNames: PropTypes.func,
};

CustomerHierarchyTreeView.defaultProps = {
  hierarchyClassNames: '',
  enableOnScrollLoad: false,
  scrollToId: null,
  autoHeight: false,
  cellHeight: _.noop(),
  onScrollOptions: {},
  getClassNames: () => [],
  iconsClassNameMap: {
    expanded: 'grid-expanded-icon',
    collapsed: 'grid-expand-icon',
    lastChild: '',
  },
  onMountCallback: _.noop,
};
